import type { Scenario } from './types';
import { validateScenario } from './scenarioValidator';

export interface CatalogEntry {
  id: string;
  path: string;
  scenario: Scenario;
}

export interface ScenarioCatalog {
  list(): readonly Scenario[];
  entries(): readonly CatalogEntry[];
  byId(id: string): Scenario | null;
  byPath(path: string): Scenario | null;
}

const EMPTY_CATALOG: ScenarioCatalog = {
  list: () => [],
  entries: () => [],
  byId: () => null,
  byPath: () => null,
};

/** Strip the .json extension to produce a stable id from a manifest path. */
function pathToId(path: string): string {
  return path.replace(/\.json$/i, '');
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    p.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e as Error); },
    );
  });
}

/**
 * Fetch the manifest at `${baseUrl}index.json`, then fetch + validate each
 * listed scenario file. Files that fail to fetch or fail validation are
 * skipped with a console warning; the rest are returned in manifest order.
 */
export async function loadCatalog(
  baseUrl: string,
  options: { fetcher?: typeof fetch; timeoutMs?: number } = {},
): Promise<ScenarioCatalog> {
  const fetcher = options.fetcher ?? fetch;
  const timeoutMs = options.timeoutMs ?? 10000;

  const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

  try {
    return await withTimeout(loadCatalogInner(normalized, fetcher), timeoutMs, `loadCatalog(${normalized})`);
  } catch (e) {
    console.error(`[scenario-catalog] failed to load from ${normalized}:`, e);
    return EMPTY_CATALOG;
  }
}

async function loadCatalogInner(
  baseUrl: string,
  fetcher: typeof fetch,
): Promise<ScenarioCatalog> {
  const indexUrl = `${baseUrl}index.json`;

  let manifest: unknown;
  try {
    const res = await fetcher(indexUrl);
    if (!res.ok) {
      console.error(`[scenario-catalog] manifest ${indexUrl} returned HTTP ${res.status}`);
      return EMPTY_CATALOG;
    }
    manifest = await res.json();
  } catch (e) {
    console.error(`[scenario-catalog] failed to fetch ${indexUrl}:`, e);
    return EMPTY_CATALOG;
  }

  if (!Array.isArray(manifest) || !manifest.every((p): p is string => typeof p === 'string')) {
    console.error(`[scenario-catalog] manifest ${indexUrl} must be an array of strings`);
    return EMPTY_CATALOG;
  }

  const entries: CatalogEntry[] = [];
  for (const path of manifest) {
    const url = `${baseUrl}${path}`;
    try {
      const res = await fetcher(url);
      if (!res.ok) {
        console.warn(`[scenario-catalog] skipping ${path}: HTTP ${res.status}`);
        continue;
      }
      const raw: unknown = await res.json();
      const result = validateScenario(raw);
      if (!result.ok) {
        console.warn(`[scenario-catalog] skipping ${path}: invalid scenario`, result.errors);
        continue;
      }
      entries.push({ id: pathToId(path), path, scenario: result.scenario });
    } catch (e) {
      console.warn(`[scenario-catalog] failed to load ${path}:`, e);
    }
  }

  const scenarios = entries.map((e) => e.scenario);
  return {
    list: () => scenarios,
    entries: () => entries,
    byId: (id) => entries.find((e) => e.id === id)?.scenario ?? null,
    byPath: (path) => entries.find((e) => e.path === path)?.scenario ?? null,
  };
}

// ── Module-level cache for the three known catalogs ────────────────────────

let mainCatalog: ScenarioCatalog = EMPTY_CATALOG;
let tutorialCatalog: ScenarioCatalog = EMPTY_CATALOG;
let campaignCatalog: ScenarioCatalog = EMPTY_CATALOG;

/** Load and cache all three catalogs. Call once during BootScene. */
export async function initCatalogs(
  baseUrl = '/scenarios/',
  options?: { fetcher?: typeof fetch; timeoutMs?: number },
): Promise<void> {
  const root = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const [m, t, c] = await Promise.all([
    loadCatalog(root, options),
    loadCatalog(`${root}tutorial/`, options),
    loadCatalog(`${root}campaign/`, options),
  ]);
  mainCatalog = m;
  tutorialCatalog = t;
  campaignCatalog = c;
}

export function getMainCatalog(): ScenarioCatalog {
  return mainCatalog;
}

export function getTutorialCatalog(): ScenarioCatalog {
  return tutorialCatalog;
}

export function getCampaignCatalog(): ScenarioCatalog {
  return campaignCatalog;
}

/** Reset cached catalogs (for tests). */
export function _resetCatalogs(): void {
  mainCatalog = EMPTY_CATALOG;
  tutorialCatalog = EMPTY_CATALOG;
  campaignCatalog = EMPTY_CATALOG;
}
